// Meta Pixel Utility - Centralized tracking for all events
// Pixel ID: 791245876708759

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

type MetaPixelEvent = 
  | 'PageView'
  | 'ViewContent'
  | 'Lead'
  | 'CompleteRegistration'
  | 'AddToCart'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase';

interface ViewContentParams {
  content_name: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}

interface LeadParams {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}

interface RegistrationParams {
  content_name?: string;
  status?: string;
  value?: number;
  currency?: string;
}

interface AddToCartParams {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
}

interface CheckoutParams {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
  num_items: number;
}

interface PurchaseParams {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
  num_items: number;
}

// Check if fbq is available
const isFbqAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

// Track PageView - automatically called by Meta Pixel base code
export const trackPageView = (): void => {
  if (isFbqAvailable()) {
    window.fbq('track', 'PageView');
    console.log('[Meta Pixel] PageView tracked');
  }
};

// Track ViewContent - when user views a product/page
export const trackViewContent = (params: ViewContentParams): void => {
  if (isFbqAvailable()) {
    window.fbq('track', 'ViewContent', params);
    console.log('[Meta Pixel] ViewContent tracked:', params);
  }
};

// Track Lead - when user shows interest (e.g., visits signup page)
export const trackLead = (params: LeadParams = {}): void => {
  if (isFbqAvailable()) {
    window.fbq('track', 'Lead', {
      content_name: params.content_name || 'Lead Capture',
      content_category: params.content_category || 'Registration',
      value: params.value || 0,
      currency: params.currency || 'INR',
    });
    console.log('[Meta Pixel] Lead tracked:', params);
  }
};

// Track CompleteRegistration - when user completes signup
export const trackCompleteRegistration = (params: RegistrationParams = {}): void => {
  if (isFbqAvailable()) {
    window.fbq('track', 'CompleteRegistration', {
      content_name: params.content_name || 'User Registration',
      status: params.status || 'success',
      value: params.value || 0,
      currency: params.currency || 'INR',
    });
    console.log('[Meta Pixel] CompleteRegistration tracked:', params);
  }
};

// Track AddToCart - when user adds product to cart
export const trackAddToCart = (params?: Partial<AddToCartParams>): void => {
  if (isFbqAvailable()) {
    const defaultParams: AddToCartParams = {
      content_name: 'Toppers Buddy Class 10 Science Notes',
      content_ids: ['SKU-TB-SCI10-2025'],
      content_type: 'product',
      value: 149,
      currency: 'INR',
    };
    window.fbq('track', 'AddToCart', { ...defaultParams, ...params });
    console.log('[Meta Pixel] AddToCart tracked:', { ...defaultParams, ...params });
  }
};

// Track InitiateCheckout - when user starts checkout
export const trackInitiateCheckout = (params?: Partial<CheckoutParams>): void => {
  if (isFbqAvailable()) {
    const defaultParams: CheckoutParams = {
      content_name: 'Toppers Buddy Class 10 Science Notes',
      content_ids: ['SKU-TB-SCI10-2025'],
      content_type: 'product',
      value: 149,
      currency: 'INR',
      num_items: 1,
    };
    window.fbq('track', 'InitiateCheckout', { ...defaultParams, ...params });
    console.log('[Meta Pixel] InitiateCheckout tracked:', { ...defaultParams, ...params });
  }
};

// Track AddPaymentInfo - when user adds payment info
export const trackAddPaymentInfo = (): void => {
  if (isFbqAvailable()) {
    window.fbq('track', 'AddPaymentInfo', {
      content_name: 'Toppers Buddy Class 10 Science Notes',
      content_ids: ['SKU-TB-SCI10-2025'],
      content_type: 'product',
      value: 149,
      currency: 'INR',
    });
    console.log('[Meta Pixel] AddPaymentInfo tracked');
  }
};

// Track Purchase - MOST IMPORTANT for conversions
export const trackPurchase = (params?: Partial<PurchaseParams>): void => {
  if (isFbqAvailable()) {
    const defaultParams: PurchaseParams = {
      content_name: 'Toppers Buddy Class 10 Science Notes',
      content_ids: ['SKU-TB-SCI10-2025'],
      content_type: 'product',
      value: 149,
      currency: 'INR',
      num_items: 1,
    };
    window.fbq('track', 'Purchase', { ...defaultParams, ...params });
    console.log('[Meta Pixel] Purchase tracked:', { ...defaultParams, ...params });
  }
};

// Default product data for consistency
export const PRODUCT_DATA = {
  content_name: 'Toppers Buddy Class 10 Science Notes',
  content_ids: ['SKU-TB-SCI10-2025'],
  content_type: 'product',
  value: 149,
  currency: 'INR',
};
